import { List } from 'immutable'
import _get from 'lodash/get'
import _isUndefined from 'lodash/isUndefined'
import { createAuthorization } from '../auth'
import {
  ROLE_AMBASSADOR_ADMIN,
  ROLE_MANAGE_PLANS_MARKETING,
  ROLE_MANAGE_PLANS_PRODUCT,
  ROLE_MARKETING_HUB,
  MODERATION_PAGE_KEY,
  PAGE_ROLE_MAPPING,
  RATE_LIMITING_PAGE_KEY,
  DIRECT_MEMBER_BASE_PAGE_KEY,
  PUBLISHING_HUB_ALLOWED_ROLE_IDS,
  ROLE_SITE_ADMIN,
} from '../auth/roles'
import { MENU_ITEMS } from '../constants'
import request from '../request'

/**
 * Get menus by platform, type, and language
 */
export async function getMenusUnder ({ auth, server, platform, type, language }) {
  const query = {}
  if (platform) {
    query.platform = platform
  }
  if (type) {
    query.type = type
  }
  if (language) {
    query.language = language
  }
  const res = await request
    .get(`${server}/v2/menu`)
    .query(query)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

export async function createMenu ({ auth, server, newMenu }) {
  const res = await request
    .post(`${server}/v2/menu`)
    .send(newMenu)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

export const getMenuPermissions = ({ config, auth }) => {
  const enableContentManagement = config.getIn(['data', 'features', 'enableContentManagement'])
  const enableContentPolicyManagement = config.getIn([
    'data',
    'features',
    'enableContentPolicyManagement',
  ])
  const enableMediaManagement = config.getIn(['data', 'features', 'enableMediaManagement'])
  const enableTextTracksBulkUpload = config.getIn([
    'data',
    'features',
    'enableTextTracksBulkUpload',
  ])
  const enableAmbassadors = config.getIn(['data', 'features', 'enableAmbassadors'])
  // const hasAccessCmsV2Permission = auth.getIn(['data', 'permissions'], List())
  //   .includes(PERM_ACCESS_CMS_V2)
  const hasAmbassadorAdminRole = auth
    .getIn(['data', 'roleIds'], List())
    .includes(ROLE_AMBASSADOR_ADMIN)

  const planCmsEnabled = !!config.getIn(['data', 'features', 'plans', 'cmsEnabled'])
  const planCmsMenuLinksEnabled = !!config.getIn([
    'data',
    'features',
    'plans',
    'cmsMenuLinksEnabled',
  ])
  const hasManagePlansRole = auth
    .getIn(['data', 'roleIds'], List())
    .some(
      (r) =>
        r === ROLE_MANAGE_PLANS_MARKETING ||
        r === ROLE_MANAGE_PLANS_PRODUCT ||
        r === ROLE_SITE_ADMIN,
    )

  const enableQaTools = config.getIn(['data', 'features', 'enableQaTools']) === true

  const hasMarketingHubRole = auth
    .getIn(['data', 'roleIds'], List())
    .some((r) => r === ROLE_SITE_ADMIN || r === ROLE_MARKETING_HUB)

  const hasPublishingHubAccess = auth
    .getIn(['data', 'roleIds'], List())
    .some((r) => PUBLISHING_HUB_ALLOWED_ROLE_IDS.includes(r))

  // Mirror the page gate so the nav link and the route can never disagree on who may enter.
  const hasModerationAccess = auth
    .getIn(['data', 'roleIds'], List())
    .some((r) => PAGE_ROLE_MAPPING[MODERATION_PAGE_KEY].includes(r))

  const hasRateLimitingAccess = auth
    .getIn(['data', 'roleIds'], List())
    .some((r) => PAGE_ROLE_MAPPING[RATE_LIMITING_PAGE_KEY].includes(r))

  const hasDirectMemberBaseAccess = auth
    .getIn(['data', 'roleIds'], List())
    .some((r) => PAGE_ROLE_MAPPING[DIRECT_MEMBER_BASE_PAGE_KEY].includes(r))

  return {
    '/content': enableContentManagement,
    '/v2/content': enableContentManagement,
    '/content-policy': enableContentPolicyManagement,
    '/media': enableMediaManagement,
    '/v2/media': enableMediaManagement,
    '/text-tracks': enableTextTracksBulkUpload,
    '/ambassadors': enableAmbassadors && hasAmbassadorAdminRole,
    '/plan-management': planCmsEnabled && planCmsMenuLinksEnabled && hasManagePlansRole,
    '/qa': enableQaTools,
    '/marketing-hub': hasMarketingHubRole,
    '/publishing-hub': hasPublishingHubAccess,
    '/moderation': hasModerationAccess,
    '/rate-limiting': hasRateLimitingAccess,
    '/member-base': hasDirectMemberBaseAccess,
  }
}

export const getFilteredMenuItems = (props) => {
  const menuPermissions = getMenuPermissions(props)

  return MENU_ITEMS.filter(
    (item) => _isUndefined(menuPermissions[item.path]) || menuPermissions[item.path],
  )
}
